import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiUsers, FiArrowRight, FiShield, FiLayers, FiCheckCircle,
  FiChevronDown, FiSettings, FiBriefcase
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const RoleHierarchy = () => {
  const personalRoles = [
    {
      name: 'Admin (Individual)',
      code: 'admin',
      level: 1,
      color: 'bg-yellow-500',
      description: 'Personal admin with advanced features',
      permissions: [
        'Full personal account access',
        'Team management',
        'Project creation/management',
        'Advanced features access'
      ]
    },
    {
      name: 'User (Individual)',
      code: 'user',
      level: 2,
      color: 'bg-green-500',
      description: 'Basic personal workspace access',
      permissions: [
        'Basic dashboard access',
        'Personal task management',
        'Limited project access',
        'Profile management'
      ]
    }
  ];

  const orgRoles = [
    {
      name: 'Enterprise Admin',
      code: 'enterprise_admin',
      level: 1,
      color: 'bg-purple-500',
      description: 'Multi-organization management',
      permissions: [
        'Manage multiple organizations',
        'Cross-org analytics',
        'Enterprise billing',
        'SSO configuration',
        'Compliance management'
      ]
    },
    {
      name: 'Organization Admin',
      code: 'org_admin',
      level: 2,
      color: 'bg-indigo-500',
      description: 'Full single organization management',
      permissions: [
        'All organization settings',
        'User management & invitations',
        'Department management',
        'Billing & subscription',
        'Security settings',
        'Organization analytics'
      ]
    },
    {
      name: 'Department Manager',
      code: 'dept_manager',
      level: 3,
      color: 'bg-blue-500',
      description: 'Manages specific department',
      permissions: [
        'Department settings',
        'Team management within dept',
        'Budget management',
        'Approve budget requests',
        'Department analytics'
      ]
    },
    {
      name: 'Team Lead',
      code: 'team_lead',
      level: 4,
      color: 'bg-cyan-500',
      description: 'Leads team within department',
      permissions: [
        'Team project management',
        'Task assignment',
        'Team member management',
        'Submit budget requests',
        'Team analytics'
      ]
    },
    {
      name: 'Employee',
      code: 'employee',
      level: 5,
      color: 'bg-teal-500',
      description: 'Standard organization member',
      permissions: [
        'Assigned task management',
        'Project participation',
        'Personal todos',
        'Submit expense requests',
        'View team activity'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/admin/roles" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/admin" className="hover:text-blue-600">Admin & Permissions</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Role Hierarchy</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl mb-4">
              <FiUsers className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Role Hierarchy</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Complete guide to all roles in Tasklina, their permissions, and when to use each one.
            </p>
          </div>

          {/* Organization Level Roles */}
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                <FiBriefcase className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Organization Roles</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Roles within organization workspaces</p>
              </div>
            </div>

            {/* Hierarchy Visual */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 mb-6">
              <div className="flex flex-col items-center space-y-2">
                {orgRoles.map((role, index) => (
                  <React.Fragment key={role.code}>
                    <div 
                      className={`px-6 py-3 rounded-xl text-white font-medium ${role.color} shadow-sm`}
                      style={{ width: `${100 - (index * 8)}%`, maxWidth: '400px' }}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <FiLayers className="w-4 h-4" />
                        <span>{role.name}</span>
                      </div>
                    </div>
                    {index < orgRoles.length - 1 && (
                      <FiChevronDown className="text-gray-400 dark:text-gray-500" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Role Details */}
            <div className="space-y-4">
              {orgRoles.map((role) => (
                <div key={role.code} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <div className={`px-4 py-3 ${role.color} text-white`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FiLayers className="w-5 h-5" />
                        <span className="font-semibold">{role.name}</span>
                      </div>
                      <code className="text-xs bg-white/20 px-2 py-1 rounded">{role.code}</code>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">{role.description}</p>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Permissions:</h4>
                    <ul className="grid md:grid-cols-2 gap-1">
                      {role.permissions.map((perm, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                          <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{perm}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Personal Workspace Roles */}
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <FiUsers className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Personal Workspace Roles</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Roles for individual users</p>
              </div>
            </div>

            {/* Role Details */}
            <div className="space-y-4">
              {personalRoles.map((role) => (
                <div key={role.code} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <div className={`px-4 py-3 ${role.color} text-white`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FiShield className="w-5 h-5" />
                        <span className="font-semibold">{role.name}</span>
                      </div>
                      <code className="text-xs bg-white/20 px-2 py-1 rounded">{role.code}</code>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">{role.description}</p>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Permissions:</h4>
                    <ul className="grid md:grid-cols-2 gap-1">
                      {role.permissions.map((perm, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                          <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{perm}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Role Assignment */}
          <section className="mb-16" id="assign-role">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Assigning Roles</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Roles can be assigned during user invitation or modified later by administrators:
            </p>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">How to Assign a Role</h3>
              <ol className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Navigate to User Management</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Go to Organization Settings → Users</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Select the User</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Click on the user's profile or select from the list</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Edit Role</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Click "Edit Role" and select the appropriate role from the dropdown</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Assign Scope (if applicable)</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">For Department Managers and Team Leads, select the specific department/team</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">5</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Save Changes</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Click Save - changes take effect immediately</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/admin/permissions"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Permission System</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/admin/security"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Security Settings</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default RoleHierarchy;
